import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SavedQuotesPanel } from "@/components/site/SavedQuotesPanel";
import { ProfileCard } from "@/components/site/ProfileCard";
import { ContentManager } from "@/components/admin/ContentManager";
import { useAccount } from "@/hooks/use-account";
import { requireConsoleAccess } from "@/lib/require-admin";
import { RolesPanel } from "@/components/admin/RolesPanel";
import { NotificationsPanel } from "@/components/site/NotificationsPanel";
import { OutreachMailbox } from "@/components/admin/OutreachMailbox";
import type { SavedQuote } from "@/lib/saved-quotes";
import {
  INQUIRY_STATUSES,
  INQUIRY_STATUS_CLASSES,
  INQUIRY_STATUS_LABELS,
  isInquiryStatus,
  updateInquiryStatus,
  type InquiryStatus,
} from "@/lib/inquiry-status";

export const Route = createFileRoute("/_authenticated/admin")({
  beforeLoad: requireConsoleAccess,
  head: () => ({
    meta: [{ title: "Inquiry Inbox | Finendra Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminPage,
});

type Submission = {
  id: string;
  name: string;
  email: string;
  company: string | null;
  service: string | null;
  source?: string | null;
  message: string;
  handled: boolean;
  handled_at: string | null;
  status: InquiryStatus;
  status_updated_at: string | null;
  created_at: string;
};

const PAGE_SIZE = 10;

function formatDate(value: string) {
  return new Date(value).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function csvCell(value: string | null) {
  return `"${(value ?? "").replace(/"/g, '""')}"`;
}

function money(value: number | null) {
  return value === null || value === undefined ? "" : String(value);
}

function downloadCsv(contents: string, filename: string) {
  const blob = new Blob([`\uFEFF${contents}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function AdminPage() {
  const navigate = useNavigate();
  const account = useAccount();
  const isAdmin = account.loading ? null : account.isAdmin;
  const canEditContent = account.loading ? null : account.canEditContent;
  const [tab, setTab] = useState<
    "mailbox" | "inquiries" | "quotes" | "content" | "roles" | "notifications" | "profile"
  >("mailbox");
  const [rows, setRows] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | InquiryStatus>("all");
  const [sort, setSort] = useState<"newest" | "oldest" | "name">("newest");
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Accounts with no staff role belong in the member dashboard.
  useEffect(() => {
    if (canEditContent === false) void navigate({ to: "/dashboard", replace: true });
  }, [canEditContent, navigate]);

  // Editors only get the content area, so start (and stay) there.
  useEffect(() => {
    if (isAdmin === false && tab !== "content" && tab !== "profile") setTab("content");
  }, [isAdmin, tab]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);

    let query = supabase
      .from("contact_submissions")
      .select("*", { count: "exact" })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    query =
      sort === "name"
        ? query.order("name", { ascending: true })
        : query.order("created_at", { ascending: sort === "oldest" });

    if (statusFilter === "open") {
      query = query.in("status", ["new", "contacted", "qualified"]);
    } else if (statusFilter !== "all") {
      query = query.eq("status", statusFilter);
    }

    if (search) {
      const term = search.replace(/[%,()]/g, " ");
      query = query.or(
        `name.ilike.%${term}%,email.ilike.%${term}%,company.ilike.%${term}%,message.ilike.%${term}%`,
      );
    }

    const { data, error: queryError, count } = await query;
    if (queryError) {
      setError("Could not load submissions.");
    } else {
      setRows((data ?? []) as Submission[]);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [isAdmin, page, search, statusFilter, sort]);

  useEffect(() => {
    void load();
  }, [load]);

  // Inbox totals per stage, independent of the active filters.
  const loadCounts = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await supabase
      .from("contact_submissions")
      .select("status")
      .limit(2000)
      .returns<{ status: InquiryStatus }[]>();
    const next: Record<string, number> = {};
    for (const row of data ?? []) next[row.status] = (next[row.status] ?? 0) + 1;
    setCounts(next);
  }, [isAdmin]);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const summary = useMemo(() => {
    if (total === 0) return "No submissions";
    const from = page * PAGE_SIZE + 1;
    const to = Math.min(total, (page + 1) * PAGE_SIZE);
    return `Showing ${from}–${to} of ${total}`;
  }, [page, total]);

  async function changeStatus(row: Submission, next: InquiryStatus) {
    if (next === row.status) return;
    setBusyId(row.id);
    setError(null);
    try {
      await updateInquiryStatus(row.id, next);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                status: next,
                status_updated_at: new Date().toISOString(),
                handled: next === "closed" || next === "spam",
              }
            : r,
        ),
      );
      void loadCounts();
      if (statusFilter !== "all") void load();
    } catch {
      setError("Could not update that inquiry's status.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(row: Submission) {
    if (!window.confirm(`Delete the inquiry from ${row.name}? This cannot be undone.`)) return;
    const { error: deleteError } = await supabase
      .from("contact_submissions")
      .delete()
      .eq("id", row.id);
    if (deleteError) {
      setError("Could not delete that submission.");
      return;
    }
    void load();
  }

  async function exportCsv() {
    let query = supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);

    if (statusFilter === "open") {
      query = query.in("status", ["new", "contacted", "qualified"]);
    } else if (statusFilter !== "all") {
      query = query.eq("status", statusFilter);
    }
    if (search) {
      const term = search.replace(/[%,()]/g, " ");
      query = query.or(
        `name.ilike.%${term}%,email.ilike.%${term}%,company.ilike.%${term}%,message.ilike.%${term}%`,
      );
    }

    const { data, error: exportError } = await query;
    if (exportError || !data) {
      setError("Could not export submissions.");
      return;
    }

    const header = "Received,Name,Email,Company,Service,Status,Status updated,Message";
    const body = (data as Submission[]).map((r) =>
      [
        csvCell(formatDate(r.created_at)),
        csvCell(r.name),
        csvCell(r.email),
        csvCell(r.company),
        csvCell(r.service),
        csvCell(INQUIRY_STATUS_LABELS[r.status]),
        csvCell(r.status_updated_at ? formatDate(r.status_updated_at) : ""),
        csvCell(r.message),
      ].join(","),
    );

    downloadCsv(
      [header, ...body].join("\n"),
      `finendra-inquiries-${new Date().toISOString().slice(0, 10)}.csv`,
    );
  }

  async function exportQuotesCsv() {
    const { data, error: exportError } = await supabase
      .from("saved_quotes")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);

    if (exportError || !data) {
      setError("Could not export saved quotes.");
      return;
    }

    const header = [
      "Saved",
      "Client",
      "Company",
      "Email",
      "Plan",
      "Status",
      "Hours",
      "Work level",
      "Hourly rate",
      "Monthly rate",
      "Billing",
      "Estimate",
      "Savings",
      "Custom",
      "Owner",
      "Note",
    ].join(",");

    const body = (data as SavedQuote[]).map((r) =>
      [
        csvCell(formatDate(r.created_at)),
        csvCell(r.client_name),
        csvCell(r.client_company),
        csvCell(r.client_email),
        csvCell(r.plan_name),
        csvCell(r.status),
        csvCell(String(r.hours)),
        csvCell(r.work_level),
        csvCell(money(r.hourly_rate)),
        csvCell(money(r.monthly_rate)),
        csvCell(r.use_retainer ? "Monthly retainer" : "Hourly"),
        csvCell(money(r.estimate)),
        csvCell(money(r.savings)),
        csvCell(r.custom ? "Yes" : "No"),
        csvCell(r.user_id ? "Signed-in visitor" : "Guest"),
        csvCell(r.note),
      ].join(","),
    );

    downloadCsv(
      [header, ...body].join("\n"),
      `finendra-saved-quotes-${new Date().toISOString().slice(0, 10)}.csv`,
    );
  }

  async function signOut() {
    await supabase.auth.signOut();
    await navigate({ to: "/auth", replace: true });
  }

  if (canEditContent !== true) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <p className="text-brand-navy/60">Checking your access…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 pt-24 md:pt-32 pb-28">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              {isAdmin ? "Admin console" : "Editor console"}
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy">
              {tab === "mailbox"
                ? "Outreach Mailbox"
                : tab === "inquiries"
                  ? "Inquiry inbox"
                  : tab === "quotes"
                    ? "Saved quotes"
                    : tab === "content"
                      ? "Website content"
                      : tab === "roles"
                        ? "Roles & permissions"
                        : tab === "notifications"
                          ? "Notifications"
                          : "My profile"}
            </h1>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/dashboard"
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              My dashboard
            </Link>
            <Link
              to="/files"
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              Files
            </Link>
            {(tab === "inquiries" || tab === "quotes") && (
              <button
                type="button"
                onClick={tab === "inquiries" ? exportCsv : exportQuotesCsv}
                className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
              >
                {tab === "inquiries" ? "Export inquiries CSV" : "Export quotes CSV"}
              </button>
            )}
            <button
              type="button"
              onClick={signOut}
              className="rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-background hover:bg-brand-accent"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-2 border-b border-brand-navy/10 pb-4">
          {(
            [
              ...(isAdmin
                ? ([
                    ["mailbox", "Mailbox"],
                    ["inquiries", "Inquiries"],
                    ["quotes", "Saved quotes"],
                  ] as const)
                : []),
              ["content", "Content"],
              ...(isAdmin ? ([["roles", "Roles"]] as const) : []),
              ["notifications", "Notifications"],
              ["profile", "Profile"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              aria-current={tab === id ? "page" : undefined}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
                tab === id
                  ? "bg-brand-navy text-background"
                  : "border border-brand-navy/15 text-brand-navy/70 hover:text-brand-accent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "mailbox" ? (
          <OutreachMailbox
            inquiries={rows}
            inquiriesLoading={loading}
            onInquiryStatusChange={changeStatus}
            onInquiryDelete={remove}
            onInquiryRefresh={load}
          />
        ) : tab === "inquiries" ? (
          <OutreachMailbox
            inquiries={rows}
            inquiriesLoading={loading}
            onInquiryStatusChange={changeStatus}
            onInquiryDelete={remove}
            onInquiryRefresh={load}
          />
        ) : tab === "quotes" ? (
          <SavedQuotesPanel />
        ) : tab === "content" ? (
          <ContentManager canPublish={account.canPublishContent} />
        ) : tab === "roles" ? (
          <RolesPanel currentUserId={account.userId} />
        ) : tab === "notifications" ? (
          <NotificationsPanel />
        ) : (
          <ProfileCard account={account} />
        )}
      </div>
    </main>
  );
}
